import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { rotableApi, type UtilizationMetrics } from '@/api/services';
import { getErrorMessage } from '@/api/client';

/**
 * Recording that a rotable went out for a run, or came back from one.
 *
 * This is the only thing that maintains a cycle count, and until now nothing in
 * the product could do it. The endpoint existed, the client method existed, and
 * no page called either — so every asset's cycle count was a seeded literal
 * that could never change, while repair-versus-replace read it as one of three
 * arguments for scrapping the asset.
 *
 * A run is not a job. `JobEquipment` records the commitment to a contract; this
 * records a trip in the hole, and a job can contain several. Returning here
 * increments the cycle count; returning from a job does not.
 */

const MOVEMENTS = [
  { value: 'ISSUE', label: 'Issued for a run', hint: 'Leaves the yard. Starts a cycle.' },
  {
    value: 'RETURN',
    label: 'Returned from a run',
    hint: 'Back in the yard. Completes a cycle and adds one to the count.',
  },
  { value: 'TRANSFER', label: 'Transferred', hint: 'Moved between yards. Not a cycle.' },
  { value: 'INSPECT', label: 'Inspected', hint: 'Examined in place. Not a cycle.' },
  { value: 'REPAIR', label: 'Sent for repair', hint: 'Marks the asset in repair.' },
  { value: 'SCRAP', label: 'Scrapped', hint: 'Retires the asset. It will not come back.' },
] as const;

const CONDITIONS = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'FAILED'] as const;

export function RecordMovementDialog({
  asset,
  onClose,
  onRecorded,
}: {
  asset: UtilizationMetrics | null;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const [movementType, setMovementType] = useState<string>('RETURN');
  const [condition, setCondition] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const selected = MOVEMENTS.find((m) => m.value === movementType);

  const save = async () => {
    if (!asset) return;
    setSaving(true);
    try {
      await rotableApi.recordMovement({
        equipmentId: asset.equipmentId,
        movementType: movementType as 'ISSUE',
        condition: (condition || undefined) as 'GOOD' | undefined,
        notes: notes || undefined,
      });
      toast.success(
        movementType === 'RETURN'
          ? `${asset.assetNumber} back in the yard — cycle recorded`
          : `${asset.assetNumber}: ${selected?.label.toLowerCase()}`
      );
      setMovementType('RETURN');
      setCondition('');
      setNotes('');
      onRecorded();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!asset} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record a movement for {asset?.assetNumber}</DialogTitle>
          <DialogDescription>
            A run, not a job. Returning from a run is what adds to the cycle count that
            repair-versus-replace reads. A job can contain several runs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="movementType">What happened</Label>
            <Select value={movementType} onValueChange={setMovementType}>
              <SelectTrigger id="movementType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MOVEMENTS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Which movements count as a cycle is the thing people get wrong,
                so the form says it rather than leaving it to be learned. */}
            {selected && <p className="text-xs text-muted-foreground">{selected.hint}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="condition">Condition, if it was assessed</Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger id="condition">
                <SelectValue placeholder="Not assessed" />
              </SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c.charAt(0) + c.slice(1).toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="movementNotes">Notes</Label>
            <Textarea
              id="movementNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Pulled after 14 hours downhole, no washout"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
